// src/components/BuyPGTModalWithNative.js
import React, { useState, useEffect } from 'react';
import { useWeb3React } from '@web3-react/core';
import './BuyPGTModal.css';

// Network configurations
const NETWORK_CONFIG = {
  1: { name: 'Ethereum Mainnet', nativeCurrency: 'ETH', nativeSymbol: 'ETH' },
  5: { name: 'Goerli Testnet', nativeCurrency: 'ETH', nativeSymbol: 'ETH' },
  137: { name: 'Polygon', nativeCurrency: 'MATIC', nativeSymbol: 'MATIC' },
  80001: { name: 'Mumbai Testnet', nativeCurrency: 'MATIC', nativeSymbol: 'MATIC' },
  8453: { name: 'Base Mainnet', nativeCurrency: 'ETH', nativeSymbol: 'ETH' },
  84532: { name: 'Base Sepolia', nativeCurrency: 'ETH', nativeSymbol: 'ETH' },
  56: { name: 'BSC Mainnet', nativeCurrency: 'BNB', nativeSymbol: 'BNB' },
  97: { name: 'BSC Testnet', nativeCurrency: 'BNB', nativeSymbol: 'BNB' },
  43114: { name: 'Avalanche C-Chain', nativeCurrency: 'AVAX', nativeSymbol: 'AVAX' },
  43113: { name: 'Avalanche Fuji', nativeCurrency: 'AVAX', nativeSymbol: 'AVAX' },
  250: { name: 'Fantom Opera', nativeCurrency: 'FTM', nativeSymbol: 'FTM' },
  4002: { name: 'Fantom Testnet', nativeCurrency: 'FTM', nativeSymbol: 'FTM' },
  42161: { name: 'Arbitrum One', nativeCurrency: 'ETH', nativeSymbol: 'ETH' },
  421613: { name: 'Arbitrum Goerli', nativeCurrency: 'ETH', nativeSymbol: 'ETH' },
  10: { name: 'Optimism', nativeCurrency: 'ETH', nativeSymbol: 'ETH' },
  420: { name: 'Optimism Goerli', nativeCurrency: 'ETH', nativeSymbol: 'ETH' },
};

// Supported ERC20 tokens by network
const SUPPORTED_TOKENS = {
  8453: [ // Base Mainnet
    {
      symbol: 'USDC',
      name: 'USD Coin',
      address: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
      decimals: 6,
      priceUSD: 1,
    },
  ],
  84532: [ // Base Sepolia
    // Add testnet tokens if available
  ],
  137: [ // Polygon
    {
      symbol: 'USDC',
      name: 'USD Coin',
      address: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174',
      decimals: 6,
      priceUSD: 1,
    },
    {
      symbol: 'USDT',
      name: 'Tether USD',
      address: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F',
      decimals: 6,
      priceUSD: 1,
    },
  ],
};

export default function BuyPGTModalWithNative({ isOpen, onClose, chainId, simpleSwapAddress }) {
  const { account } = useWeb3React();
  const [amountUSD, setAmountUSD] = useState(10);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('native');
  const [selectedToken, setSelectedToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [nativePrice, setNativePrice] = useState(2000); // Default price

  const networkInfo = NETWORK_CONFIG[chainId] || {
    name: 'Unknown Network',
    nativeCurrency: 'Native Token',
    nativeSymbol: 'NATIVE'
  };

  const supportedTokens = SUPPORTED_TOKENS[chainId] || [];

  useEffect(() => {
    if (chainId) {
      fetchNativePrice();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainId]);

  const fetchNativePrice = async () => {
    try {
      const { ethers } = await import('ethers');
      const provider = new ethers.BrowserProvider(window.ethereum);
      const swapContract = new ethers.Contract(
        simpleSwapAddress,
        [
          'function tokens(address) view returns (bool enabled, uint8 decimals, uint256 usdRate)',
        ],
        provider
      );

      // Get native currency info (address(0))
      const tokenInfo = await swapContract.tokens('0x0000000000000000000000000000000000000000');
      if (tokenInfo.enabled) {
        // usdRate is in 1e18 format
        const rate = Number(ethers.formatUnits(tokenInfo.usdRate, 18));
        setNativePrice(rate);
      }
    } catch (err) {
      console.error('Failed to fetch native price:', err);
    }
  };

  const calculateAmount = () => {
    if (selectedPaymentMethod === 'native') {
      return (amountUSD / nativePrice).toFixed(6);
    } else if (selectedToken) {
      return (amountUSD / selectedToken.priceUSD).toFixed(selectedToken.decimals);
    }
    return '0';
  };

  const buyWithNative = async () => {
    if (!account) return;
    setLoading(true);
    try {
      const { ethers } = await import('ethers');
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();

      const swapContract = new ethers.Contract(
        simpleSwapAddress,
        [
          'function buyPGTWithNative() payable',
          'function quoteNative(uint256) view returns (uint256)',
        ],
        signer
      );

      // Calculate native amount needed
      const nativeAmount = ethers.parseEther(calculateAmount());

      // Get quote
      const pgtOut = await swapContract.quoteNative(nativeAmount);
      console.log(`Buying ${ethers.formatUnits(pgtOut, 2)} PGT for ${calculateAmount()} ${networkInfo.nativeSymbol}`);

      // Execute purchase
      const tx = await swapContract.buyPGTWithNative({ value: nativeAmount });
      await tx.wait();

      alert(`✅ Successfully purchased ${ethers.formatUnits(pgtOut, 2)} PGT!`);
      onClose();
    } catch (err) {
      console.error('Buy with native failed:', err);
      alert(`Transaction failed: ${err.reason || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const buyWithToken = async () => {
    if (!account || !selectedToken) return;
    setLoading(true);
    try {
      const { ethers } = await import('ethers');
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();

      // Approve token first
      const tokenContract = new ethers.Contract(
        selectedToken.address,
        [
          'function approve(address,uint256) returns (bool)',
          'function allowance(address,address) view returns (uint256)',
        ],
        signer
      );

      const tokenAmount = ethers.parseUnits(calculateAmount(), selectedToken.decimals);

      // Check allowance
      const allowance = await tokenContract.allowance(account, simpleSwapAddress);
      if (allowance < tokenAmount) {
        console.log('Approving token...');
        const approveTx = await tokenContract.approve(simpleSwapAddress, tokenAmount);
        await approveTx.wait();
      }

      // Buy PGT
      const swapContract = new ethers.Contract(
        simpleSwapAddress,
        [
          'function buyPGT(address,uint256)',
          'function quote(address,uint256) view returns (uint256)',
        ],
        signer
      );

      const pgtOut = await swapContract.quote(selectedToken.address, tokenAmount);
      console.log(`Buying ${ethers.formatUnits(pgtOut, 2)} PGT for ${calculateAmount()} ${selectedToken.symbol}`);

      const tx = await swapContract.buyPGT(selectedToken.address, tokenAmount);
      await tx.wait();

      alert(`✅ Successfully purchased ${ethers.formatUnits(pgtOut, 2)} PGT!`);
      onClose();
    } catch (err) {
      console.error('Buy with token failed:', err);
      alert(`Transaction failed: ${err.reason || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBuy = () => {
    if (selectedPaymentMethod === 'native') {
      buyWithNative();
    } else {
      buyWithToken();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="buy-pgt-modal-overlay" onClick={onClose}>
      <div className="buy-pgt-modal" onClick={e => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>✕</button>
        <h2>Buy PGT Tokens</h2>
        <p className="price-info">1 PGT = $1.00 USD</p>
        <p className="network-info">Network: {networkInfo.name}</p>

        <div className="input-group">
          <label>Amount (USD):</label>
          <input
            type="number"
            min="1"
            step="1"
            value={amountUSD}
            onChange={e => setAmountUSD(Math.max(1, Number(e.target.value)))}
          />
          <small>You will receive: {amountUSD} PGT</small>
        </div>

        <div className="input-group">
          <label>Pay with:</label>
          <div className="payment-method-selector">
            <button
              className={selectedPaymentMethod === 'native' ? 'method-btn active' : 'method-btn'}
              onClick={() => setSelectedPaymentMethod('native')}
            >
              {networkInfo.nativeSymbol} (Native)
            </button>
            {supportedTokens.length > 0 && (
              <button
                className={selectedPaymentMethod === 'token' ? 'method-btn active' : 'method-btn'}
                onClick={() => {
                  setSelectedPaymentMethod('token');
                  setSelectedToken(supportedTokens[0]);
                }}
              >
                Stablecoins
              </button>
            )}
          </div>
        </div>

        {selectedPaymentMethod === 'token' && supportedTokens.length > 0 && (
          <div className="input-group">
            <label>Select Token:</label>
            <select
              value={selectedToken?.symbol || ''}
              onChange={e => {
                const token = supportedTokens.find(t => t.symbol === e.target.value);
                setSelectedToken(token);
              }}
            >
              {supportedTokens.map(t => (
                <option key={t.symbol} value={t.symbol}>
                  {t.name} ({t.symbol})
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="calculation-display">
          <p>
            You will pay: <strong>{calculateAmount()}</strong> {' '}
            {selectedPaymentMethod === 'native' ? networkInfo.nativeSymbol : selectedToken?.symbol}
          </p>
          {selectedPaymentMethod === 'native' && (
            <small>Current {networkInfo.nativeSymbol} price: ${nativePrice.toLocaleString()}</small>
          )}
        </div>

        <button
          className="buy-btn"
          onClick={handleBuy}
          disabled={loading || (selectedPaymentMethod === 'token' && !selectedToken)}
        >
          {loading ? 'Processing...' : `Buy ${amountUSD} PGT`}
        </button>

        <div className="modal-footer">
          <small>
            Gas fees apply. Transaction will be processed on {networkInfo.name}.
          </small>
        </div>
      </div>
    </div>
  );
}
